/**
 * useRecordsBrowser
 *
 * Data layer for the Records list-detail page. Merges two sources so the list
 * scales to millions yet reflects edits immediately:
 *
 *  1. Firestore "top" — the live registry cache (recent records, real-time).
 *     Filtered client-side by status/group/query so an edit (e.g. status change)
 *     adds/removes the row instantly. This is the freshness guarantee.
 *  2. Algolia "body" — faceted, filtered, paginated search/browse over the full
 *     collection. Empty query = browse (recency-ranked); typed query = typeahead.
 *
 * Results are deduped by docId, Firestore-top first (authoritative + fresh),
 * then Algolia pages appended via loadMore(). Selection state lives in
 * recordsBrowserState and is reset whenever the active filter changes.
 *
 * The registry snapshot is resolved to this entity's single cache bucket before
 * anything memoizes on it; `registrySnap.records` (the map across all entities)
 * must never reach a dependency array or an unrelated entity's refresh rebuilds
 * this list.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import debounce from 'lodash/debounce';

import type { EntityRecord } from '#types';
import { RecordStatus, DEFAULT_SEARCH_HITS_PER_PAGE, DEFAULT_SEARCH_DEBOUNCE_MS } from '#types/System.Parameters.types';

import { searchClient } from '@/services/searchConfig';
import { algoliaConfig } from '@/config';
import { refreshRecords, subscribe } from '@/store/entityRecordsRegistry/actions';
import { cacheKey, entityRecordsRegistryStore } from '@/store/entityRecordsRegistry/store';
import { resetRecordSelection } from './recordsBrowserState';

interface AlgoliaRecordHit {
    objectID: string;
    name?: string;
    status?: RecordStatus;
    entityGroupIds?: string[];
    meta?: EntityRecord['meta'];
}

interface AlgoliaPage {
    hits: AlgoliaRecordHit[];
    nbPages: number;
}

export interface UseRecordsBrowserParams {
    entityId: string;
    query: string;
    statusFilters: RecordStatus[];
    groupIds: string[];
}

export interface UseRecordsBrowserReturn {
    records: EntityRecord[];
    hasMore: boolean;
    loadMore: () => void;
    loading: boolean;
    error: Error | null;
    refresh: () => void;
}

const EMPTY_PAGE: AlgoliaPage = { hits: [], nbPages: 0 };

/** Stable identity for "no cached bucket", so the merge memo below does not churn. */
const EMPTY_RECORDS: EntityRecord[] = [];

/** Status-less base filter; the status clause is appended per query. */
const buildFilters = (entityId: string, groupIds: string[]): string => {
    const clauses: string[] = ['_collectionName:records', `_parentDocId:${entityId}`];
    if (groupIds.length > 0) {
        clauses.push(`(${groupIds.map((g) => `entityGroupIds:${g}`).join(' OR ')})`);
    }
    return clauses.join(' AND ');
};

/** Fetches one page of records matching the entity + group + status + query filter. */
const fetchAlgoliaPage = async (
    entityId: string,
    query: string,
    statusFilters: RecordStatus[],
    groupIds: string[],
    page: number,
): Promise<AlgoliaPage> => {
    if (!searchClient || !algoliaConfig?.allIndexName) return EMPTY_PAGE;

    const base = buildFilters(entityId, groupIds);
    const filters = statusFilters.length > 0
        ? `${base} AND (${statusFilters.map((s) => `status:${s}`).join(' OR ')})`
        : base;

    const { results } = await searchClient.search<AlgoliaRecordHit>({
        requests: [{
            indexName: algoliaConfig.allIndexName,
            query,
            filters,
            page,
            hitsPerPage: DEFAULT_SEARCH_HITS_PER_PAGE,
        }],
    });

    const hitsResult = results[0];
    if (!hitsResult || !('hits' in hitsResult)) return EMPTY_PAGE;

    return {
        hits: hitsResult.hits as AlgoliaRecordHit[],
        nbPages: hitsResult.nbPages ?? 0,
    };
};

const hitToRecord = (hit: AlgoliaRecordHit): EntityRecord => ({
    docId: hit.objectID,
    name: hit.name || '',
    status: hit.status,
    entityGroupIds: hit.entityGroupIds,
    meta: hit.meta,
});

export const useRecordsBrowser = ({
    entityId,
    query,
    statusFilters,
    groupIds,
}: UseRecordsBrowserParams): UseRecordsBrowserReturn => {
    useEffect(() => {
        if (!entityId) return;
        return subscribe(entityId);
    }, [entityId]);

    const registrySnap = useSnapshot(entityRecordsRegistryStore);
    const cacheRecords = (entityId
        ? (registrySnap.records.get(cacheKey(entityId)) as EntityRecord[] | undefined)
        : undefined) ?? EMPTY_RECORDS;

    const groupKey = groupIds.join(',');
    const groups = useMemo(() => (groupKey ? groupKey.split(',') : []), [groupKey]);

    const statusKey = statusFilters.join(',');
    const statuses = useMemo(() => (statusKey ? (statusKey.split(',') as RecordStatus[]) : []), [statusKey]);

    const queryTrimmed = query.trim();
    const [debouncedQuery, setDebouncedQuery] = useState(queryTrimmed);
    const commitQuery = useMemo(() => debounce(setDebouncedQuery, DEFAULT_SEARCH_DEBOUNCE_MS), []);
    useEffect(() => {
        commitQuery(queryTrimmed);
        return () => commitQuery.cancel();
    }, [queryTrimmed, commitQuery]);

    /** Algolia (paginated body) tracks the debounced query; the cache top filters on the immediate query. */
    const algoliaSignature = `${entityId}|${debouncedQuery.toLowerCase()}|${statusKey}|${groupKey}`;

    const [page, setPage] = useState(0);
    const [pages, setPages] = useState<AlgoliaPage[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [refreshNonce, setRefreshNonce] = useState(0);
    const seqRef = useRef(0);

    useEffect(() => {
        resetRecordSelection();
        setPage(0);
        setPages([]);
    }, [algoliaSignature]);

    useEffect(() => {
        if (!entityId) return;
        const seq = ++seqRef.current;
        setLoading(true);
        fetchAlgoliaPage(entityId, debouncedQuery, statuses, groups, page)
            .then((result) => {
                if (seqRef.current !== seq) return;
                setPages((prev) => (page === 0 ? [result] : [...prev, result]));
                setError(null);
            })
            .catch((err) => {
                if (seqRef.current !== seq) return;
                setError(err instanceof Error ? err : new Error('Search failed'));
            })
            .finally(() => {
                if (seqRef.current === seq) setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId, algoliaSignature, page, refreshNonce]);

    const queryLower = queryTrimmed.toLowerCase();
    const matchesFilters = useCallback(
        (record: EntityRecord): boolean => {
            if (statuses.length > 0 && (!record.status || !statuses.includes(record.status))) return false;
            if (groups.length > 0 && !groups.some((g) => record.entityGroupIds?.includes(g))) return false;
            if (queryLower) {
                const name = record.meta?.searchNameLowercase || record.name?.toLowerCase() || '';
                if (!name.includes(queryLower)) return false;
            }
            return true;
        },
        [statuses, groups, queryLower],
    );

    /**
     * Algolia body rows, mapped once per page set. Keeping their object identity
     * stable across cache changes means a name edit in the live "top" re-renders
     * only the edited row, not every (memoized) body row.
     */
    const bodyRecords = useMemo<EntityRecord[]>(
        () => pages.flatMap((p) => p.hits).filter((h) => h.objectID).map(hitToRecord),
        [pages],
    );

    const records = useMemo<EntityRecord[]>(() => {
        const top = cacheRecords.filter(matchesFilters);
        const topIds = new Set(top.map((r) => r.docId));
        const body = bodyRecords.filter((r) => !topIds.has(r.docId as string));
        return [...top, ...body];
    }, [cacheRecords, matchesFilters, bodyRecords]);

    const nbPages = pages[0]?.nbPages ?? 0;
    const hasMore = page + 1 < nbPages;

    const loadMore = useCallback(() => {
        if (loading) return;
        setPage((p) => p + 1);
    }, [loading]);

    const refresh = useCallback(() => {
        if (entityId) void refreshRecords(entityId);
        setPage(0);
        setPages([]);
        setRefreshNonce((n) => n + 1);
    }, [entityId]);

    return {
        records,
        hasMore,
        loadMore,
        loading,
        error,
        refresh,
    };
};
